// SwapRequestsPage.jsx — staff sees swap requests they sent / received.
import React, { useCallback, useEffect, useState } from "react";
import MessengerLayout from "../../layout/MessengerLayout";
import { ArrowLeftRight, Check, X, Trash2 } from "lucide-react";
import { toast } from "../../components/ui/Toast";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON = "#00FF99";
const AMBER = "#EF9F27";
const RED  = "#E24B4A";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export default function SwapRequestsPage({ embedded = false }) {
  const [items, setItems] = useState([]);
  const [tab,   setTab]   = useState("incoming");

  const load = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/rota/swaps`, { headers: authHeaders() });
    if (r.ok) setItems(await r.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  const respond = async (id, status) => {
    const msg = status === "declined" ? window.prompt("Decline message (optional)") : "";
    const r = await fetch(`${API_BASE}/api/rota/swaps/${id}/respond`, {
      method: "PATCH",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ status, message: msg || null }),
    });
    if (r.ok) { load(); toast.success(`Swap ${status}`); }
    else toast.error("Failed to respond to swap");
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this swap request?")) return;
    await respond(id, "cancelled");
  };

  const incoming = items.filter((x) => x.my_role === "incoming");
  const outgoing = items.filter((x) => x.my_role === "outgoing");

  const inner = (
    <div style={{ maxWidth: 760, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: "rgba(0,255,153,0.1)", color: NEON,
          display: "flex", alignItems: "center", justifyContent: "center",
          border: `0.5px solid ${NEON}33`,
        }}>
          <ArrowLeftRight size={16} />
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: "#fff" }}>Shift swaps</h1>
          <p style={{ fontSize: 11, color: "#666", margin: "2px 0 0 0" }}>
            {incoming.length} incoming · {outgoing.length} outgoing
          </p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 14, borderBottom: "0.5px solid #1c1c1c", paddingBottom: 8 }}>
        {[["incoming", "Incoming"], ["outgoing", "Outgoing"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: "5px 14px", borderRadius: 999, fontSize: 11,
            background: tab === k ? "rgba(0,255,153,0.08)" : "transparent",
            border: tab === k ? `0.5px solid ${NEON}55` : "0.5px solid #2a2a2a",
            color: tab === k ? NEON : "#888", cursor: "pointer",
          }}>{l}</button>
        ))}
      </div>

      {(tab === "incoming" ? incoming : outgoing).length === 0 && (
        <p style={{ color: "#555", fontSize: 12 }}>No {tab} requests.</p>
      )}

      {(tab === "incoming" ? incoming : outgoing).map((s) => (
        <SwapCard key={s.id} swap={s} side={tab}
          onAccept={() => respond(s.id, "accepted")}
          onDecline={() => respond(s.id, "declined")}
          onCancel={() => cancel(s.id)} />
      ))}
    </div>
  );

  return embedded ? inner : <MessengerLayout>{inner}</MessengerLayout>;
}

function SwapCard({ swap, side, onAccept, onDecline, onCancel }) {
  const statusColor =
    swap.status === "pending"  ? AMBER :
    swap.status === "accepted" ? NEON  :
    swap.status === "declined" ? RED   : "#666";

  return (
    <div style={{
      padding: 12, marginBottom: 10, borderRadius: 12,
      background: "#0d0d0d", border: "0.5px solid #1c1c1c",
    }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: "#ddd", fontWeight: 500 }}>
          {side === "incoming" ? swap.requester_name : swap.target_name}
        </span>
        <span style={{
          marginLeft: 8, padding: "1px 8px", borderRadius: 4, fontSize: 9,
          textTransform: "uppercase", letterSpacing: 0.5,
          background: `${statusColor}1a`, color: statusColor,
        }}>{swap.status}</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 10, color: "#555" }}>
          {new Date(swap.created_at).toLocaleString()}
        </span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 11, color: "#aaa" }}>
        <ShiftPill s={swap.requester_shift} />
        <ArrowLeftRight size={12} color="#666" />
        <ShiftPill s={swap.target_shift} />
      </div>

      {swap.requester_message && (
        <p style={{ fontSize: 11, color: "#888", marginTop: 8, marginBottom: 0 }}>
          "{swap.requester_message}"
        </p>
      )}

      {swap.status === "pending" && side === "incoming" && (
        <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
          <button onClick={onAccept} style={btn(NEON)}>
            <Check size={11} /> Accept
          </button>
          <button onClick={onDecline} style={btn(RED)}>
            <X size={11} /> Decline
          </button>
        </div>
      )}
      {swap.status === "pending" && side === "outgoing" && (
        <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
          <button onClick={onCancel} style={btn("#888")}>
            <Trash2 size={11} /> Cancel
          </button>
        </div>
      )}
    </div>
  );
}

function ShiftPill({ s }) {
  if (!s) return <span style={{ color: "#555" }}>—</span>;
  return (
    <span style={{
      padding: "3px 10px", borderRadius: 6,
      background: "rgba(255,255,255,0.04)", border: "0.5px solid #2a2a2a",
    }}>
      {s.shift_date} · <strong style={{ color: "#ddd" }}>{s.shift_type}</strong>
      {s.shift_type !== "off" && ` ${s.start_time}-${s.end_time}`}
    </span>
  );
}

const btn = (color) => ({
  display: "inline-flex", alignItems: "center", gap: 4,
  padding: "5px 12px", fontSize: 11, borderRadius: 8,
  background: color === NEON ? "rgba(0,255,153,0.12)" :
              color === RED  ? "rgba(226,75,74,0.12)" : "rgba(255,255,255,0.04)",
  border: `0.5px solid ${color}55`, color, cursor: "pointer",
});
